import { useEffect, useRef, useState } from 'react';
import type { AreaSummary, AvailableResource } from '../types';

export function Counts({ summary }: { summary: AreaSummary }) {
  const entries = [
    { key: 'free', label: 'Free', value: summary.free },
    { key: 'half', label: 'Half free', value: summary.half_free },
    { key: 'taken', label: 'Taken', value: summary.taken },
    { key: 'capacity', label: 'Total', value: summary.capacity },
  ] as const;

  return (
    <div className="counts">
      {entries.map((entry) => (
        <div key={entry.key} className={`count ${entry.key}`}>
          <div className="n">{entry.value}</div>
          <div className="k">{entry.label}</div>
        </div>
      ))}
    </div>
  );
}

export function Legend() {
  const items = [
    ['free', 'Free'],
    ['half', 'Half free'],
    ['taken', 'Taken'],
    ['mine', 'Yours'],
    ['blocked', 'Unavailable'],
  ] as const;

  return (
    <div className="legend">
      {items.map(([key, label]) => (
        <span key={key}>
          <i className="swatch" style={{ background: `var(--${key})` }} />
          {label}
        </span>
      ))}
    </div>
  );
}

interface MapProps {
  mapKey: string;
  resources: AvailableResource[];
  selectedId: string | null;
  onSelect: (resource: AvailableResource) => void;
}

export function FloorMap({ mapKey, resources, selectedId, onSelect }: MapProps) {
  const [width, setWidth] = useState(900);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Fit the plan to the container on first paint, then leave it alone
  // so zooming does not reset while someone is hunting for a desk.
  useEffect(() => {
    const element = wrapRef.current;
    if (!element) return;
    setWidth(Math.max(element.clientWidth - 8, 320));
  }, [mapKey]);

  const placed = resources.filter((resource) => resource.map !== null);

  return (
    <div className="stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <Legend />
        <div className="row" style={{ gap: 6 }}>
          <button className="quiet" onClick={() => setWidth((w) => Math.max(320, w - 200))} aria-label="Zoom out">
            −
          </button>
          <button className="quiet" onClick={() => setWidth((w) => Math.min(3200, w + 200))} aria-label="Zoom in">
            +
          </button>
        </div>
      </div>

      <div className="map-wrap" ref={wrapRef} style={{ maxHeight: '62vh' }}>
        <div className="map-inner" style={{ width }}>
          <img src={`/maps/${mapKey.split('/').pop()}`} alt="" width={width} />
          {placed.map((resource) => {
            const chosen = resource.id === selectedId;
            const tone = resource.mine ? 'mine' : resource.state;
            const detail = resource.mine
              ? 'yours'
              : (resource.heldBy ?? resource.zone ?? null);
            return (
              <button
                key={resource.id}
                className={`spot ${tone}${chosen ? ' chosen' : ''}`}
                style={{ left: `${resource.map?.x}%`, top: `${resource.map?.y}%` }}
                disabled={!resource.bookable && !resource.mine}
                onClick={() => onSelect(resource)}
                aria-label={
                  detail ? `Desk ${resource.label}, ${detail}` : `Desk ${resource.label}`
                }
              >
                <span className="tip" aria-hidden="true">
                  {resource.label}
                  {detail && <span className="who"> · {detail}</span>}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {placed.length === 0 && (
        <p className="muted">
          Nothing has been positioned on this plan yet. Use the list instead.
        </p>
      )}
    </div>
  );
}
